(() => {
  'use strict';

  if (window.GasNeedsCart) return;

  const STORAGE_KEY = 'gasneeds:catalog-cart:v2';
  const LEGACY_KEY = 'gasneeds:availability-basket:v1';
  const MAX_QUANTITY = 10000;
  let cachedItems = [];
  let storageError = '';

  const validHandle = (value) => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(value);
  const validQuantity = (value) => Number.isSafeInteger(Number(value)) && Number(value) >= 1 && Number(value) <= MAX_QUANTITY;

  function clean(item) {
    if (!item || !Number.isSafeInteger(Number(item.id)) || Number(item.id) < 1 || !validHandle(item.handle) || !validQuantity(item.quantity)) return null;

    let image = '';
    try {
      const parsed = new URL(String(item.image || ''), window.location.origin);
      if (item.image && (parsed.protocol === 'https:' || parsed.origin === window.location.origin)) image = parsed.href;
    } catch (_) {
      // An image is optional; malformed image data is discarded.
    }

    return {
      id: Number(item.id),
      handle: item.handle,
      title: String(item.title || item.handle).trim().slice(0, 500),
      variant: String(item.variant || '').replace(/^Default Title$/, '').trim().slice(0, 250),
      sku: String(item.sku || '').trim().slice(0, 250),
      quantity: Number(item.quantity),
      image,
      requiresAvailability: item.requiresAvailability === true || item.requiresAvailability === 'true'
    };
  }

  function normalise(items) {
    const unique = new Map();
    items.forEach((item) => {
      const value = clean(item);
      if (!value) throw new Error('Invalid saved product data.');
      const existing = unique.get(value.id);
      if (existing) existing.quantity = Math.min(MAX_QUANTITY, existing.quantity + value.quantity);
      else unique.set(value.id, value);
    });
    return [...unique.values()];
  }

  function readPayload(key) {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !Array.isArray(data.items)) throw new Error('Invalid saved cart.');
    return normalise(data.items);
  }

  function read() {
    try {
      let items = readPayload(STORAGE_KEY);
      if (!items) {
        const legacy = readPayload(LEGACY_KEY);
        items = legacy || [];
        if (legacy) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({version: 2, items}));
          localStorage.removeItem(LEGACY_KEY);
        }
      }
      cachedItems = items;
      storageError = '';
    } catch (_) {
      storageError = 'This browser could not load your saved cart. Enable site storage, reload the page, or contact our team.';
    }
    return cachedItems.map((item) => ({...item}));
  }

  function productUrl(item) {
    const root = window.routes?.rootUrl || '/';
    return `${root}products/${encodeURIComponent(item.handle)}?variant=${item.id}`;
  }

  function itemCount() {
    return cachedItems.reduce((total, item) => total + item.quantity, 0);
  }

  function updateBadges() {
    const count = itemCount();
    document.querySelectorAll('.header__cart-count').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = false;
    });
    document.querySelectorAll('[data-gn-count]').forEach((node) => {
      node.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
    });
  }

  function notify(external = false) {
    updateBadges();
    document.dispatchEvent(new CustomEvent('gn:catalog-cart-changed', {
      detail: {items: read(), count: itemCount(), error: storageError, external}
    }));
  }

  function write(change) {
    read();
    if (storageError) throw new Error(storageError);
    const next = normalise(change(cachedItems.map((item) => ({...item}))));
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({version: 2, items: next}));
    } catch (_) {
      throw new Error('This browser could not save your cart. Enable site storage and try again.');
    }
    cachedItems = next;
    storageError = '';
    notify();
    return read();
  }

  const api = {
    maxQuantity: MAX_QUANTITY,
    getItems: read,
    getError: () => storageError,
    getCount: () => itemCount(),
    productUrl,
    add(item) {
      const value = clean(item);
      if (!value) throw new Error('Could not add this product. Reload the page and try again.');
      let finalQuantity = value.quantity;
      write((items) => {
        const existing = items.find((entry) => entry.id === value.id);
        if (existing) {
          finalQuantity = existing.quantity + value.quantity;
          if (finalQuantity > MAX_QUANTITY) throw new Error(`The maximum quantity is ${MAX_QUANTITY.toLocaleString()}.`);
          existing.quantity = finalQuantity;
          existing.requiresAvailability ||= value.requiresAvailability;
          existing.title = value.title || existing.title;
          existing.variant = value.variant || existing.variant;
          existing.sku = value.sku || existing.sku;
          existing.image = value.image || existing.image;
        } else {
          items.push(value);
        }
        return items;
      });
      return finalQuantity;
    },
    setQuantity(id, quantity) {
      if (!validQuantity(quantity)) throw new Error(`Enter a whole quantity from 1 to ${MAX_QUANTITY.toLocaleString()}.`);
      return write((items) => {
        const item = items.find((entry) => entry.id === Number(id));
        if (!item) throw new Error('This product is no longer in your cart.');
        item.quantity = Number(quantity);
        return items;
      });
    },
    remove(id) {
      return write((items) => items.filter((entry) => entry.id !== Number(id)));
    },
    clear() {
      return write(() => []);
    },
    refresh: notify,
    syncMini: notify,
    setNativeCount() {
      // Kept for compatibility with cached theme sections; quote mode uses one local cart.
      updateBadges();
    }
  };

  window.GasNeedsCart = api;
  window.GasNeedsQuote = api;

  read();
  const boot = () => notify();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once: true});
  else boot();
  document.addEventListener('shopify:section:load', boot);
  window.addEventListener('pageshow', () => { read(); notify(true); });
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY || event.key === LEGACY_KEY || event.key === null) {
      read();
      notify(true);
    }
  });
})();
